/**
 * TrainerLayout - Wrapper for all trainer/admin routes
 *
 * Combines TrainerGuard authentication with Layout.
 * Uses React Router's Outlet to render child routes, keeping
 * the Layout (sidebar, header) mounted across page navigations.
 *
 * The Suspense boundary is inside Layout so that when lazy-loaded
 * routes are loading, only the content area shows a spinner while
 * the sidebar stays visible.
 */

import { Suspense } from "react";
import { Outlet } from "react-router-dom";
import { TrainerGuard } from "./TrainerGuard";
import { SubscriptionGuard } from "./SubscriptionGuard";
import Layout from "./Layout";
import { PageLoadingSpinner } from "./PageLoadingSpinner";
import { TrainerTermsGate } from "./TrainerTermsGate";
import { useTrainerTerms } from "@/hooks/use-trainer-terms";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Consent gate, mounted inside TrainerGuard so it only ever runs for a
 * signed-in trainer, and OUTSIDE Layout so the gate replaces the app rather
 * than rendering inside the sidebar chrome — a consent screen you can navigate
 * away from is not a gate.
 *
 * Deliberately sits before SubscriptionGuard: agreeing to the Terms should not
 * require an active subscription, and a trainer sent to billing first would hit
 * payment flows governed by terms they have not accepted.
 */
function TermsGated({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { needsAcceptance, loading, accept } = useTrainerTerms(user?.id);

  if (loading) return <PageLoadingSpinner />;
  if (needsAcceptance) return <TrainerTermsGate onAccept={accept} />;
  return <>{children}</>;
}

export function TrainerLayout() {
  return (
    <TrainerGuard>
      <TermsGated>
        <SubscriptionGuard>
          <Layout>
            <Suspense fallback={<PageLoadingSpinner />}>
              <Outlet />
            </Suspense>
          </Layout>
        </SubscriptionGuard>
      </TermsGated>
    </TrainerGuard>
  );
}
